/**
 * Admin Finance Dashboard
 * Platform KPIs, payout approvals, transaction logs
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  DollarSign,
  TrendingUp,
  Users,
  Store,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Filter,
  Loader2,
  Eye
} from 'lucide-react';

const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
};

const AdminFinance = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [transTypeFilter, setTransTypeFilter] = useState('all');
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab, userTypeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, withdrawalsRes, transactionsRes] = await Promise.all([
        api.get('/finance/admin/summary'),
        api.get(`/finance/admin/withdrawals?status=${activeTab === 'all' ? '' : activeTab}&user_type=${userTypeFilter === 'all' ? '' : userTypeFilter}`),
        api.get('/finance/admin/transactions?limit=50')
      ]);
      
      setSummary(summaryRes.data);
      setWithdrawals(withdrawalsRes.data.withdrawals || []);
      setTransactions(transactionsRes.data.transactions || []);
    } catch (error) {
      console.error('Failed to load admin finance data');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (withdrawalId) => {
    setProcessing(withdrawalId);
    try {
      await api.post(`/finance/admin/withdrawals/${withdrawalId}/approve`);
      toast.success('Withdrawal approved and paid!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (withdrawalId) => {
    setProcessing(withdrawalId);
    try {
      await api.post(`/finance/admin/withdrawals/${withdrawalId}/reject`);
      toast.success('Withdrawal rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100';
      case 'approved': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-amber-600 bg-amber-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'completed': return 'text-green-600 bg-green-100';
      default: return 'text-gray-300 bg-gray-100';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'driver_earning':
      case 'driver_payout':
      case 'driver_tip':
        return <Truck className="h-4 w-4" />;
      case 'merchant_earning':
      case 'merchant_payout':
        return <Store className="h-4 w-4" />;
      case 'platform_fee':
        return <DollarSign className="h-4 w-4" />;
      default:
        return <ArrowUpRight className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount) => {
    return `J$${(amount || 0).toLocaleString()}`;
  };

  if (loading && !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-6 bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 px-5 py-4 shadow-lg border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-gray-700 rounded-full"
          >
            <ArrowLeft className="h-5 w-5 text-gray-300" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-white">Finance Dashboard</h1>
            <p className="text-xs text-gray-400">Platform financial overview</p>
          </div>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          className="rounded-lg"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      <main className="p-4 space-y-4">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3">
          {/* Total GMV */}
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-5 w-5 text-[#00A86B]" />
              <span className="text-xs text-gray-400">All Time</span>
            </div>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary?.total_gmv)}</p>
            <p className="text-xs text-gray-400">Total GMV</p>
          </div>
          
          {/* Platform Revenue */}
          <div className="bg-gradient-to-br from-[#00A86B] to-[#008B5A] rounded-2xl p-4 shadow-sm text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="h-5 w-5" />
              <span className="text-xs opacity-70">Revenue</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary?.platform_revenue)}</p>
            <p className="text-xs opacity-70">Platform Fees</p>
          </div>
          
          {/* Driver Payouts */}
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <Truck className="h-5 w-5 text-blue-500" />
              <span className="text-xs text-gray-400">Drivers</span>
            </div>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary?.total_driver_payouts)}</p>
            <p className="text-xs text-gray-400">Total Paid Out</p>
          </div>
          
          {/* Merchant Payouts */}
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <Store className="h-5 w-5 text-purple-500" />
              <span className="text-xs text-gray-400">Merchants</span>
            </div>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary?.total_merchant_payouts)}</p>
            <p className="text-xs text-gray-400">Total Paid Out</p>
          </div>
        </div>

        {/* Period Stats */}
        <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
          <h3 className="font-bold text-white mb-3">Period Performance</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-gray-700 rounded-xl">
              <p className="text-lg font-bold text-white">{formatCurrency(summary?.today_gmv)}</p>
              <p className="text-xs text-gray-400">Today GMV</p>
              <p className="text-xs text-[#00A86B] font-medium">{formatCurrency(summary?.today_revenue)} rev</p>
            </div>
            <div className="text-center p-3 bg-gray-700 rounded-xl">
              <p className="text-lg font-bold text-white">{formatCurrency(summary?.week_gmv)}</p>
              <p className="text-xs text-gray-400">Week GMV</p>
              <p className="text-xs text-[#00A86B] font-medium">{formatCurrency(summary?.week_revenue)} rev</p>
            </div>
            <div className="text-center p-3 bg-gray-700 rounded-xl">
              <p className="text-lg font-bold text-white">{formatCurrency(summary?.month_gmv)}</p>
              <p className="text-xs text-gray-400">Month GMV</p>
              <p className="text-xs text-[#00A86B] font-medium">{formatCurrency(summary?.month_revenue)} rev</p>
            </div>
          </div>
        </div>

        {/* Pending Alert */}
        {summary?.pending_withdrawal_count > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
            <Clock className="h-6 w-6 text-amber-500" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">
                {summary.pending_withdrawal_count} Pending Withdrawal{summary.pending_withdrawal_count > 1 ? 's' : ''}
              </p>
              <p className="text-sm text-amber-600">
                Drivers: {formatCurrency(summary?.pending_driver_withdrawals)} | 
                Merchants: {formatCurrency(summary?.pending_merchant_withdrawals)}
              </p>
            </div>
          </div>
        )}

        {/* Withdrawal Queue */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-white">Withdrawal Requests</h3>
              <div className="flex gap-2">
                <select
                  value={userTypeFilter}
                  onChange={(e) => setUserTypeFilter(e.target.value)}
                  className="text-xs border border-gray-700 rounded-lg px-2 py-1"
                >
                  <option value="all">All Users</option>
                  <option value="driver">Drivers</option>
                  <option value="merchant">Merchants</option>
                </select>
              </div>
            </div>
            
            {/* Status Tabs */}
            <div className="flex gap-2">
              {['pending', 'paid', 'rejected', 'all'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeTab === tab
                      ? 'bg-[#00A86B] text-white'
                      : 'bg-gray-100 text-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
          
          {withdrawals.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <CheckCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No {activeTab === 'all' ? '' : activeTab} withdrawals</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {withdrawals.map((w) => (
                <div key={w.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        w.user_type === 'driver' ? 'bg-blue-100' : 'bg-purple-100'
                      }`}>
                        {w.user_type === 'driver' ? (
                          <Truck className={`h-5 w-5 ${w.user_type === 'driver' ? 'text-blue-600' : 'text-purple-600'}`} />
                        ) : (
                          <Store className="h-5 w-5 text-purple-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{formatCurrency(w.amount)}</p>
                        <p className="text-xs text-gray-400">
                          {w.user_type} • {w.method} • {new Date(w.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(w.status)}`}>
                        {w.status}
                      </span>
                      
                      {w.status === 'pending' && (
                        <>
                          <Button
                            onClick={() => handleApprove(w.id)}
                            disabled={processing === w.id}
                            size="sm"
                            className="rounded-lg text-white text-xs"
                            style={{ background: COLORS.green }}
                          >
                            {processing === w.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <CheckCircle className="h-3 w-3" />
                            )}
                          </Button>
                          <Button
                            onClick={() => handleReject(w.id)}
                            disabled={processing === w.id}
                            size="sm"
                            variant="outline"
                            className="rounded-lg text-red-500 border-red-200 hover:bg-red-50 text-xs"
                          >
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {w.admin_note && (
                    <p className="text-xs text-gray-400 mt-1 pl-13">Note: {w.admin_note}</p>
                  )}
                  {w.payout_reference && (
                    <p className="text-xs text-gray-400 mt-1 pl-13">Ref: {w.payout_reference}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Transaction Log */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="font-bold text-white">Transaction Log</h3>
            <select
              value={transTypeFilter}
              onChange={(e) => setTransTypeFilter(e.target.value)}
              className="text-xs border border-gray-700 rounded-lg px-2 py-1"
            >
              <option value="all">All Types</option>
              <option value="customer_payment">Payments</option>
              <option value="platform_fee">Platform Fees</option>
              <option value="driver_earning">Driver Earnings</option>
              <option value="driver_tip">Driver Tips</option>
              <option value="driver_payout">Driver Payouts</option>
              <option value="merchant_earning">Merchant Earnings</option>
              <option value="merchant_payout">Merchant Payouts</option>
            </select>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">User</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-400">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions
                  .filter(t => transTypeFilter === 'all' || t.type === transTypeFilter)
                  .slice(0, 20)
                  .map((t) => (
                    <tr key={t.id} className="hover:bg-gray-700">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`p-1 rounded ${
                            t.direction === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {getTypeIcon(t.type)}
                          </span>
                          <span className="text-xs text-gray-300">{t.type.replace(/_/g, ' ')}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-300">
                        {t.user_type} • {t.user_id.slice(0, 8)}...
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${
                        t.direction === 'credit' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {t.direction === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(t.status)}`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-gray-400">
                        {new Date(t.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminFinance;
